<template>
  <div id="academic_undergrad_ba">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro :copy="this.intro"></usc-intro>
    <usc-table :title="this.table.title" :data="this.table.data" :disclaimer="this.table.disclaimer"></usc-table>
    <div class="body mb gutter-lg">
      <p>The <a href="https://catalogue.usc.edu/" class="inline-hyperlink" target="_blank">USC Catalogue</a> provides a complete list of the university’s courses, as well as other information on curriculum, admissions, policies and academics.</p>
    </div>
    <usc-course-chart :dataSet="courseData">
      <img slot="image" src="../../../static/USC_Marshall_CurriculumMap_2023_BUAD_R5.png" alt="USC Marshall Undergraduate Business Administration Program Four-Year Sample Course Sequence">
    </usc-course-chart>
  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import table from '@/components/Table'
import calloutLink from '@/components/CalloutLink'
import CourseChart from '@/components/CourseChart/CourseChart'
import videoEmbed from '@/components/videoEmbed'
import Cta from '@/components/Cta'

export default {
  name: 'UndergradBA',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-table': table,
    'usc-calloutLink': calloutLink,
    'usc-course-chart': CourseChart,
    'usc-videoEmbed': videoEmbed,
    'usc-cta': Cta
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'Undergraduate Business Administration',
        loResSrc: '../../../static/HeroImages/Academics3.jpg',
        hiResSrc: '../../../static/HeroImages/Academics3.jpg'
      },
      intro: [
        'Marshall’s Business Administration degree consists of three sets of courses: general education requirements taken by all undergraduates to fulfill the university’s liberal arts requirements; gateway courses toward the business curriculum; and free electives. The business core curriculum provides a broad understanding of business fundamentals and lays the foundation for upper-division Marshall electives.'
      ],
      table: {
        title: 'Core Classes',
        data: [
          {
            left: 'Introduction to Financial Accounting',
            right: 'BUAD 280'
          },
          {
            left: 'Introduction to Managerial Accounting',
            right: 'BUAD 281'
          },
          {
            left: 'Communication Strategy in Business',
            right: 'BUAD 302'
          },
          {
            left: 'Organizational Behavior and Leadership',
            right: 'BUAD 304'
          },
          {
            left: 'Business Finance or Advanced Business Finance',
            right: 'BUAD 306 or BUAD 308'
          },
          {
            left: 'Marketing Fundamentals',
            right: 'BUAD 307'
          },
          {
            left: 'Applied Business Statistics or Statistics and Data Science for Business',
            right: 'BUAD 310 or BUAD 312'
          },
          {
            left: 'Operations Management or Advanced Operations Management and Analytics',
            right: 'BUAD 311 or BUAD 313'
          },
          {
            left: 'Economic Analysis for Business Decisions or Microeconomics for Business',
            right: ''
          },
          {
            left: 'Macroeconomic Analysis for Business Decisions or Macroeconomics for Business',
            right: ''
          },
          {
            left: 'Strategic Management',
            right: 'BUAD 497'
          },
          {
            left: 'Fundamental Principles of Calculus or Calculus I',
            right: ''
          }
        ],
        disclaimer: ''
      },
      calloutLink: {
        title: 'Consult the current USC Catalogue online for detailed course descriptions',
        url: 'http://catalogue.usc.edu/',
        urlText: 'The USC catalogue for fine prints'
      },
      courseData: {
        title: 'USC Marshall Undergraduate Business Administration Program',
        subtitle: 'Four-Year Sample Course Sequence',
        key: [
          {
            courseColor: 'marshall-core',
            courseLabel: 'Marshall Core'
          },
          {
            courseColor: 'usc-requirement',
            courseLabel: 'USC Requirement'
          },
          {
            courseColor: 'marshall-elective',
            courseLabel: 'Marshall Elective'
          },
          {
            courseColor: 'usc-elective',
            courseLabel: 'USC Elective/Minor'
          }
        ],
        years: {
          freshman: {
            yearTitle: 'FRESHMAN',
            semesters: {
              Fall: [
                {
                  courseID: 'Math 118',
                  courseName: 'Fundamental Principles of Calculus',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 304',
                  courseName: 'Organizational Behavior and Leadership',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 150',
                  courseName: 'Writing and Critical Reasoning',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'ECON 351x',
                  courseName: 'Microeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 310 or 312',
                  courseName: 'Applied Business Stats or Stats & Data Science for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '4',
                  courseCredits: 4
                },
                {
                  courseID: 'GESM',
                  courseName: 'General Education Seminar',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Free Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                },
                {
                  courseID: 'Optional Elective',
                  courseName: 'BUAD 104',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          },
          sophmore: {
            yearTitle: 'SOPHOMORE',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 280',
                  courseName: 'Introduction to Financial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'BUAD 302',
                  courseName: 'Communication Strategy in Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Optional Elective',
                  courseName: 'BUAD 252',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 281',
                  courseName: 'Introduction to Managerial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'ECON 352x',
                  courseName: 'Macroeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          },
          junior: {
            yearTitle: 'JUNIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 306 or 308',
                  courseName: 'Business Finance or Advanced Business Finance',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 307',
                  courseName: 'Marketing Fundamentals',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 311 or 313',
                  courseName: 'Operations Mgmt or Advanced operations Mgmt & Analytics',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 340',
                  courseName: 'Advanced Writing',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          },
          senior: {
            yearTitle: 'SENIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 425',
                  courseName: 'Introduction to Business Analytics',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 497',
                  courseName: 'Strategic Management',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Elective/Minor',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          }
        }
      },
      video: {
        caption: 'How marketing and advertising students helped the L.A. Kings pull off their Stanley Cup win.',
        src: 'https://player.vimeo.com/video/249449491?title=0&byline=0&portrait=0',
        frameTitle: 'Video: How marketing and advertising students helped the L.A. Kings pull off their Stanley Cup win.'
      }
    }
  },
  created () {
    this.$root.setPageHeader('Academics', 'Undergraduate Business Administration Program')
  }
}
</script>

<style scoped lang="scss">
</style>



// WEBPACK FOOTER //
// src/pages/academics/UndergradBA.vue